import { useUserSession } from '@solinkcorp/authentication';
import EventActions from 'actions/EventActions';
import { setRange } from 'actions/filterActions';
import { VideoContext } from 'components/Contexts';
import GlobalDatePicker from 'components/GlobalDatePicker/Aspen/GlobalDatePicker';
import { usePlayingCamerasContext } from 'components/PlayingCamerasContext';
import { type FC, useCallback, useContext, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useControllerState } from 'solox';
import { isKiosk } from 'src/core/utils/kioskUtils';
import useLocale from 'src/hooks/useLocale';
import { useDispatch, useSelector } from 'stores/store';
import FeaturesUtil from 'utils/FeaturesUtil';
import { getTabFromPathname, SIDEKICK_URL } from 'utils/urls';
import {
  RANGE_DAY,
  SD_RESTRICTION_THRESHOLD,
  TAB_CLIPS,
  TAB_EVENTS,
  TAB_VIDEO,
} from 'vd-constants';
import {
  buildRangeOptions,
  chevronsStepByRange,
  getDateBounds,
  isDateFilterRoute,
} from './dateFilterPolicy';

export { isDateFilterRoute };

export interface AspenDateFilterProps {
  variant: 'expanded' | 'collapsed';
  onLiveToggle: (toggle: boolean) => void;
}

const AspenDateFilter: FC<AspenDateFilterProps> = ({ variant, onLiveToggle }) => {
  const locale = useLocale();
  const { pathname } = useLocation();
  const dispatch = useDispatch();
  const user = useUserSession();
  const { isLiveToggleOn } = useContext(VideoContext);
  const { locationIds, timezoneId } = useSelector((state) => state.filter);
  const playingCamerasController = usePlayingCamerasContext();
  const numberOfCamerasPlaying = useControllerState(
    playingCamerasController.state,
    (state) => state.playingCameraIds.length,
  );

  const tabName = getTabFromPathname(pathname);
  const isSidekick = pathname.startsWith(SIDEKICK_URL);

  const rangeOptions = useMemo(() => buildRangeOptions({ pathname, locale }), [pathname, locale]);

  const { videoPlaybackStartsAt, videoPlaybackEndsAt } = user.profile.metadata;
  const { minDate, maxDate } = getDateBounds({
    pathname,
    timezoneId,
    videoPlaybackStartsAt,
    videoPlaybackEndsAt,
  });

  const closeEventPlayer = useCallback(() => {
    playingCamerasController.clearPlayingCameras();
    EventActions.closeEventPlayer();
  }, [playingCamerasController]);

  // Sidekick 'last 7 days' always ends now, so a picked date only means something for Day.
  const switchToDayRange = useCallback(() => dispatch(setRange(RANGE_DAY)), [dispatch]);

  if (!isDateFilterRoute(pathname)) {
    return null;
  }

  const tooManyCameras = numberOfCamerasPlaying > SD_RESTRICTION_THRESHOLD;
  const liveViewOnly = FeaturesUtil.isLiveViewOnly();
  const isTableView = pathname === '/clips/table';
  const canGoLive =
    tabName === TAB_VIDEO &&
    !videoPlaybackEndsAt &&
    (!isKiosk() || FeaturesUtil.canPlayLiveInKiosk()) &&
    !liveViewOnly;
  // Go Live lives in the popover. During playback, too many cameras locks the date but leaves the trigger enabled.
  const playbackNeedsGoLive = tooManyCameras && !isLiveToggleOn && canGoLive;

  return (
    <GlobalDatePicker
      variant={variant}
      handleToggleClick={() => onLiveToggle(false)}
      isOnInToggle={!isLiveToggleOn}
      showLiveToggle={tabName === TAB_VIDEO}
      onGoLive={canGoLive ? () => onLiveToggle(true) : undefined}
      minDate={minDate}
      maxDate={maxDate}
      selectedLocationIds={locationIds}
      disabled={liveViewOnly || isTableView || (tooManyCameras && !playbackNeedsGoLive)}
      lockDateSelection={playbackNeedsGoLive}
      onChangeRangeDate={
        tabName === TAB_EVENTS || tabName === TAB_CLIPS ? closeEventPlayer : undefined
      }
      onDatePicked={isSidekick ? switchToDayRange : undefined}
      showTimeInput
      rangeOptions={rangeOptions}
      rangeRolling={isSidekick}
      navigateByRange={chevronsStepByRange(pathname)}
    />
  );
};

export default AspenDateFilter;
